import React, { FC, useContext, useEffect } from 'react';
import { ResponsiveDialog } from '@shared/components/ResponsiveDialog';
import { ButtonV2, Flex, JoyKitThemeProvider, LinkV2, LinkV2Props, Spinner, TextV2 } from '@withjoy/joykit';
import { AnalyticsContext, useTranslation } from '@shared/core';
import { Close } from '@withjoy/joykit/icons';
import { drawerOverrides } from '@shared/components/ResponsiveMenu/ResponsiveMenu.styles';
import { pxToRem } from '@withjoy/joykit/theme';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { StarRating } from '@shared/components';
import { withWindow } from '@shared/utils/withWindow';
import { LinkConfirmationDialog } from '../LinkConfirmationDialog';
import { HOTEL_IMAGE_PLACEHOLDER } from '@shared/utils/constants';
import { useHotelSuggestionDialog } from '../hooks/useHotelSuggestionDialog';
import { HotelSuggestionDialogProps, HotelSuggestionDialogRootProps, HotelSuggestionDialogWithChildrenProps } from './HotelSuggestionDialog.types';

const HotelSuggestionDialogRoot: FC<HotelSuggestionDialogRootProps> = props => {
  const { isOpen, onClose, href, isLoadingHotelMatchData, hotelMatchData, isOpenLinkConfirmationDialog, onCloseLinkConfirmationDialog } = props;

  const { t } = useTranslation('joykit');
  const hotelSuggestionDialogTransFn = t('hotelSuggestionDialog');

  const analytics = useContext(AnalyticsContext);

  const onViewHotelClick = useEventCallback(() => {
    analytics.track({ action: 'ButtonInteracted', category: 'guestRateAvailableModal', extraInfo: { name: 'JoyHotelLink', url: hotelMatchData?.findHotelMatch?.link } });
    withWindow(window => {
      window.open(hotelMatchData?.findHotelMatch?.link, 'hotelSuggestionLink');
    });
  });

  const onOpenExternalLinkClick = useEventCallback(() => {
    analytics.track({ action: 'ButtonInteracted', category: 'guestRateAvailableModal', extraInfo: { name: 'ExternalLink', url: href } });
    withWindow(window => {
      window.open(href, 'hotelSuggestionExternalLink');
    });
  });

  return (
    <>
      <JoyKitThemeProvider>
        <ResponsiveDialog
          isOpen={isOpen}
          onClose={onClose}
          hasCloseButton={false}
          removeBodyPadding={true}
          hideFooterDivider={true}
          dialogOverrides={{
            Content: { props: { width: '100%', maxWidth: pxToRem(560), paddingX: 9 } },
            Body: { props: { paddingX: 0, paddingY: 9, paddingBottom: 6 } },
            Footer: { props: { paddingX: { _: 6, sm: 6 }, paddingY: 0 } }
          }}
          drawerOverrides={{
            ...drawerOverrides,
            Content: {
              props: {
                ...drawerOverrides.Content?.props,
                paddingY: 0
              }
            },
            Body: {
              props: {
                ...drawerOverrides.Body?.props,
                paddingX: 6,
                paddingTop: 6,
                paddingBottom: 7
              }
            },
            Footer: { props: { paddingX: 6, paddingTop: 6 } }
          }}
          renderFooter={
            isLoadingHotelMatchData || !hotelMatchData?.findHotelMatch
              ? undefined
              : () => (
                  <Flex width="100%" flexDirection="column" gap={4} paddingBottom={10}>
                    <ButtonV2 variant="outline" size="lg" shape="rounded" intent="neutral" onClick={onOpenExternalLinkClick}>
                      {hotelSuggestionDialogTransFn.continueToExternalSite()}
                    </ButtonV2>
                    <ButtonV2 variant="outline" size="lg" shape="rounded" intent="neutral" onClick={onViewHotelClick}>
                      {hotelSuggestionDialogTransFn.checkRatesOnJoy()}
                    </ButtonV2>
                  </Flex>
                )
          }
        >
          {isLoadingHotelMatchData || !hotelMatchData?.findHotelMatch ? (
            <Flex marginBottom={5} justifyContent="center">
              <Spinner />
            </Flex>
          ) : (
            <Flex flexDirection="column" gap={6} alignItems="flex-start" paddingX={{ _: 0, sm: 6 }} paddingTop={{ _: pxToRem(56), sm: 0 }}>
              <Flex position="absolute" top={7} right={7} cursor="pointer" _hover={{ color: 'mono12' }} _active={{ color: 'mono13' }}>
                <Close size={24} onClick={onClose} />
              </Flex>
              <Flex flexDirection={{ _: 'column-reverse', sm: 'column' }} gap={{ _: 6, sm: 5 }} alignItems="flex-start">
                <Flex flexDirection="column" gap={{ _: 5, sm: 3 }} alignItems={{ _: 'flex-start', sm: 'center' }}>
                  <TextV2 typographyVariant={'hed4'} fontSize={{ _: pxToRem(20), sm: pxToRem(26) }} color="mono14">
                    {hotelSuggestionDialogTransFn.title()}
                  </TextV2>
                  <Flex flexDirection="column" width="100%" gap={7}>
                    <TextV2 typographyVariant="body1" color="mono12" textAlign={{ _: 'left', sm: 'center' }}>
                      {hotelSuggestionDialogTransFn.descriptionLine1({
                        HotelName: () => <span style={{ fontStyle: 'italic', fontWeight: 400 }}>{hotelMatchData?.findHotelMatch?.name}</span>
                      })}
                    </TextV2>
                    <TextV2 typographyVariant="body1" color="mono12" textAlign={{ _: 'left', sm: 'center' }}>
                      {hotelSuggestionDialogTransFn.descriptionLine2()}
                    </TextV2>
                  </Flex>
                </Flex>

                <Flex
                  flexDirection="row"
                  width="100%"
                  minHeight={pxToRem(130)}
                  paddingX={6}
                  paddingY={5}
                  gap={4}
                  borderRadius={4}
                  border="1px solid"
                  borderColor="mono3"
                  backgroundColor="white"
                >
                  <Flex
                    flexShrink={0}
                    width={pxToRem(126)}
                    height="auto"
                    borderRadius={pxToRem(12)}
                    background={`url("${hotelMatchData?.findHotelMatch?.thumbnailUrl || HOTEL_IMAGE_PLACEHOLDER}") lightgray 50%`}
                    backgroundSize="cover"
                    backgroundRepeat="no-repeat"
                  />

                  <Flex flexDirection="column" gap={3} alignItems="flex-start">
                    <TextV2 typographyVariant="hed1" color="mono14">
                      {hotelMatchData?.findHotelMatch?.name}
                    </TextV2>
                    <Flex flexDirection="column" gap={3} alignItems="flex-start" justifyContent="center">
                      {hotelMatchData?.findHotelMatch?.rating && (
                        <StarRating id="star-rating-hotel-suggestion-dialog" rating={hotelMatchData.findHotelMatch.rating} hideNonFilledStars showPartialRating={false} />
                      )}
                      <TextV2 typographyVariant="button3" fontSize={pxToRem(14)} color="mono10">
                        {`${hotelMatchData?.findHotelMatch?.address.address.trim()}, ${hotelMatchData?.findHotelMatch?.address.city.trim()}`}
                      </TextV2>
                    </Flex>
                  </Flex>
                </Flex>
              </Flex>
            </Flex>
          )}
        </ResponsiveDialog>
      </JoyKitThemeProvider>
      {/* fallback when the hotel match is not found, we will show the link confirmation dialog */}
      <LinkConfirmationDialog href={href} isOpen={isOpenLinkConfirmationDialog} onClose={onCloseLinkConfirmationDialog} />
    </>
  );
};

/**
 * This component is used to wrap the links and show the hotel suggestion dialog when the link is clicked.
 */
export const HotelSuggestionDialogWithChildren: FC<HotelSuggestionDialogWithChildrenProps> = ({ href: hrefProp, onClick, children, isAllowListedUrl, ...restProps }) => {
  const {
    isOpen,
    onOpen,
    onClose,
    isOpenLinkConfirmationDialog,
    onOpenLinkConfirmationDialog,
    onCloseLinkConfirmationDialog,
    openSafeLinkUrl,
    openHotelSuggestionDialog,
    findHotelMatch,
    hotelMatchData,
    hotelMatchCalled,
    isLoadingHotelMatchData,
    href
  } = useHotelSuggestionDialog({ hrefProp, isAllowListedUrl });

  const linkProps = {
    textAlign: 'left',
    ...restProps,
    as: 'button',
    onClick: (...args) => {
      if (!href) {
        onClose();
        return;
      }
      onClick?.(...args);
      if (!hotelMatchCalled) {
        onOpen();
        findHotelMatch({ variables: { url: href } });
      } else if (hotelMatchData?.findHotelMatch) {
        openHotelSuggestionDialog();
      } else {
        isAllowListedUrl ? openSafeLinkUrl() : onOpenLinkConfirmationDialog();
      }
    }
  } as LinkV2Props;

  return (
    <>
      <LinkV2 {...linkProps}>{children}</LinkV2>
      <HotelSuggestionDialogRoot
        isOpen={isOpen}
        onClose={onClose}
        isLoadingHotelMatchData={isLoadingHotelMatchData}
        hotelMatchData={hotelMatchData}
        href={href}
        isOpenLinkConfirmationDialog={isOpenLinkConfirmationDialog}
        onCloseLinkConfirmationDialog={onCloseLinkConfirmationDialog}
      />
    </>
  );
};

/**
 * This component is a standalone component that can be used to show the hotel suggestion dialog using the isOpen prop.
 * This is typically used in scenarios where the hotel suggestion dialog is not wrapped by a link. eg: custom pages dangerouslySetInnerHTML cases
 */
export const HotelSuggestionDialog: FC<HotelSuggestionDialogProps> = ({ href: hrefProp, isOpen, onClose, isAllowListedUrl }) => {
  const {
    isOpen: isOpenHotelSuggestionDialog,
    onOpen: onOpenHotelSuggestionDialog,
    onClose: onCloseHotelSuggestionDialog,
    isOpenLinkConfirmationDialog,
    onOpenLinkConfirmationDialog,
    onCloseLinkConfirmationDialog,
    openSafeLinkUrl,
    openHotelSuggestionDialog,
    findHotelMatch,
    hotelMatchData,
    hotelMatchCalled,
    isLoadingHotelMatchData,
    href
  } = useHotelSuggestionDialog({ hrefProp, isAllowListedUrl, onCloseDialog: onClose });

  const handleCloseHotelSuggestionDialog = useEventCallback(() => {
    onCloseHotelSuggestionDialog();
    onClose();
  });

  const handleCloseLinkConfirmationDialog = useEventCallback(() => {
    onCloseLinkConfirmationDialog();
    onClose();
  });

  useEffect(() => {
    if (isOpen) {
      if (!href) {
        handleCloseHotelSuggestionDialog();
        return;
      }
      if (!hotelMatchCalled) {
        onOpenHotelSuggestionDialog();
        findHotelMatch({ variables: { url: href } });
      } else if (hotelMatchData?.findHotelMatch) {
        openHotelSuggestionDialog();
      } else {
        isAllowListedUrl ? openSafeLinkUrl() : onOpenLinkConfirmationDialog();
        onClose();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <HotelSuggestionDialogRoot
      isOpen={isOpenHotelSuggestionDialog}
      onClose={handleCloseHotelSuggestionDialog}
      isLoadingHotelMatchData={isLoadingHotelMatchData}
      hotelMatchData={hotelMatchData}
      href={href}
      isOpenLinkConfirmationDialog={isOpenLinkConfirmationDialog}
      onCloseLinkConfirmationDialog={handleCloseLinkConfirmationDialog}
    />
  );
};

HotelSuggestionDialogRoot.displayName = 'HotelSuggestionDialogRoot';
HotelSuggestionDialogWithChildren.displayName = 'HotelSuggestionDialogWithChildren';
HotelSuggestionDialog.displayName = 'HotelSuggestionDialog';
